import * as React from 'react'
import styled from 'styled-components'
import useCart from 'hooks/account/cart/useCart'
import theme from 'theme'
import Button from 'theme/atoms/Button'
import PromotionInput from './PromotionInput'
import DeliveryInfoModal from './DeliveryInfoModal'
import CartPriceTable from 'theme/molecules/account/CartPriceTable'
import ShareCart from './ShareCart'
import PaypalExpress from './PaypalExpress'
import B2cSwitch from 'theme/atoms/B2cSwitch'
import useTranslations from 'hooks/useTranslations'
import * as orderMinimum from 'utils/orderMinimum'
import MinimumOrderValue from 'theme/molecules/MinimumOrderValue/MinimumOrderValue'
import priceStr from 'utils/priceString'
import ButtonRateLimiterDecorator from 'theme/atoms/Decorators/ButtonRateLimiterDecorator'

export type Props = {
  btnLabel?: string
  headline?: string
  showDeliveryCost?: boolean
  showPaypalButton?: boolean
  onBtnClick?: () => void
  showShareCart?: boolean
  ecoTax?: { ecoTaxNet: number; ecoTaxGross: number }
  isRestricted?: boolean
  hidePromoInput?: boolean
  orderMinimumCheck?: boolean
  showOrderProgressBar?: boolean
}

export default function CartSummary(props: Props) {
  const t = useTranslations<'molecules-account-CartSummary'>()
  const cart = useCart()
  const [infoModal, setInfoModal] = React.useState(false)

  const isOrderMinimumNotReached = props.orderMinimumCheck
    ? orderMinimum.notReached(cart.data.positionPrice)
    : false

  const buttonDisabled = props.isRestricted || isOrderMinimumNotReached

  return (
    <Wrapper
      className="CartSummary"
      data-cy-ctx="molecules/account/CartSummary"
    >
      <div className="summary">
        {props.headline && (
          <h2 data-cy-state="show-cart-summary-headline">{props.headline}</h2>
        )}
        <CartPriceTable
          loading={!cart.setup}
          specialDelivery={cart.data.specialDelivery}
          deliveryCost={cart.data.deliveryCost}
          positionPrice={cart.data.positionPrice}
          tax={cart.data.tax}
          taxRate={cart.data.taxRate}
          totalPrice={cart.data.totalPrice}
          ecoTax={props.ecoTax}
          deliveryReduction={cart.data.deliveryReduction}
        />
        {props.showOrderProgressBar && isOrderMinimumNotReached && (
          <MinimumOrderValue
            data-cy-state="show-order-value-progress-bar"
            orderValue={cart.data.positionPrice}
            minimumOrderValue={orderMinimum.value() || 0}
            minimum_order_value_snippet={t('minimum_order_value', {
              value: (orderMinimum.value() || 0) - cart.data.positionPrice,
              minimumValue: orderMinimum.value() || 0,
              MINIMUMTEXT: '_',
              __transformers: {
                MINIMUMTEXT: (_, vars) => <b>{vars.MINIMUMTEXT_VALUE}</b>,
                minimumValue: (val) => <b>{priceStr(val)}</b>,
                value: (val) => <b>{priceStr(val)}</b>
              }
            })}
          />
        )}
        {props.onBtnClick && props.btnLabel && (
          <div className="cart-btn-with-info" data-cy-state="cart-btn-visible">
            <ButtonRateLimiterDecorator>
              <Button
                variation={
                  buttonDisabled ? 'cart_channel_restriction' : 'primary'
                }
                data-cy-handle="cart-summary-action-btn"
                onClick={() => props.onBtnClick && props.onBtnClick()}
                disabled={buttonDisabled}
              >
                {props.btnLabel}
              </Button>
            </ButtonRateLimiterDecorator>
          </div>
        )}
        <B2cSwitch.B2C>
          {!buttonDisabled && props.showPaypalButton && (
            <div
              data-cy-state="show-paypal-btn"
              className="paypal-express-button"
            >
              <PaypalExpress />
            </div>
          )}
        </B2cSwitch.B2C>
      </div>

      {!props.hidePromoInput && (
        <span data-cy-state="show-promo-input">
          <PromotionInput />
        </span>
      )}

      {props.showShareCart && <ShareCart data-cy-state="show-share-cart" />}
      {infoModal && (
        <DeliveryInfoModal
          data-cy-state="show-delivery-costs-modal"
          onClose={() => setInfoModal(false)}
        />
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  z-index: ${theme.zIndex.theme_molecules_account_cartsummary};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing('m')};
  width: 100%;
  @media (min-width: ${theme.ms.MD}px) {
    position: sticky;
    top: 0;
  }
  .CartPriceTable {
    padding-bottom: ${theme.spacing('xs')};
  }

  .MinimumOrderValue {
    padding-top: ${theme.spacing('m')};
  }

  > .summary {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing('xs')};
    border: 1px solid ${theme.colors.shade.b6};
    padding: ${theme.spacing('m')};
    width: 100%;
    background: ${theme.colors.white};

    > h2 {
      ${theme.ty('rc-2xl', '700')};
      @media (min-width: ${theme.ms.MD}px) {
        ${theme.ty('rc-3xl', '700')};
      }
    }
    .croatiaCurrency {
      margin-top: -10px;
      color: #625d52;
      ${theme.ty('r-s', '700')}
      text-align: right;
      flex: none;
      order: 0;
      flex-grow: 0;
    }
    > .cart-btn-with-info {
      display: flex;
      flex-direction: column;
      width: 100%;
      /* @media (min-width: ${theme.ms.MD}px) { */
      margin-top: ${theme.spacing('m')};
      /* } */
      > .info {
        display: flex;
        margin-top: 0;
        padding-bottom: ${theme.spacing('m')};
        gap: ${theme.spacing('xxs')};
        color: ${theme.colors.accent.blue};
        align-items: center;
        ${theme.ty('r-xs', null, 'r-s')}
        > svg {
          width: 15px;
          height: 15px;
          circle {
            fill: ${theme.colors.accent.blue};
          }
        }
      }
    }
  }
`
